import { createContext, useContext, useEffect } from 'react'
import type { BreadcrumbItem } from '@mysaiva/core/components'

export interface BreadcrumbState {
  /** Page-provided trail; `null` means "derive from the page registry". */
  override: BreadcrumbItem[] | null
  setOverride: (items: BreadcrumbItem[] | null) => void
}

export const BreadcrumbContext = createContext<BreadcrumbState | null>(null)

export function useBreadcrumbOverride(): BreadcrumbItem[] | null {
  return useContext(BreadcrumbContext)?.override ?? null
}

/**
 * Lets a page replace the default `Workspace / <page>` trail, e.g. the key
 * detail page shows `API Keys / Production reader`. Cleared on unmount.
 * Pass `null` (or nothing) while the label is still loading to keep the
 * default trail.
 */
export function useWorkspaceBreadcrumb(items: BreadcrumbItem[] | null | undefined): void {
  const ctx = useContext(BreadcrumbContext)
  const setOverride = ctx?.setOverride
  const serialized = JSON.stringify(
    items?.map(item => ({
      label: typeof item.label === 'string' ? item.label : '',
      href: item.href
    }))
  )

  useEffect(() => {
    if (!setOverride) return
    setOverride(items ?? null)
    return () => setOverride(null)
    // The serialized form tracks label/href changes without re-running on identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOverride, serialized])
}
